import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import CheckIcon from "../../icons/Check";
import CloseIcon from "../../icons/Close";
import { Item, Viewport } from "./styles";

export type ToastKind = "success" | "error" | "info";

export type ToastOptions = {
  /** A second line, in gray (e.g. the link made). */
  description?: string;
  /** One button beside the text (e.g. "Copiar"); clicking it closes the toast. */
  action?: { label: string; onClick: () => void };
  /** Milliseconds on screen: 4 s, an error 6 s. */
  duration?: number;
};

type Toast = ToastOptions & { id: number; kind: ToastKind; title: string; leaving: boolean };

type ToastApi = Record<ToastKind, (title: string, options?: ToastOptions) => number> & {
  dismiss: (id: number) => void;
};

const DURATION = { success: 4000, info: 4000, error: 6000 };
const MAX_SHOWN = 3;
const LEAVE_MS = 200;
// A "!" or an "i" drawn as thick as the app's check (never a character of the font, which changes from one computer
// to another)
const Mark = ({ dot }: { dot: "top" | "bottom" }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={3}
    strokeLinecap="round">
    <path d={dot === "bottom" ? "M12 7v6" : "M12 11v6"} />
    <path d={dot === "bottom" ? "M12 17h.01" : "M12 7h.01"} />
  </svg>
);

const ICONS = {
  success: <CheckIcon width={13} height={13} color="currentColor" />,
  error: <Mark dot="bottom" />,
  info: <Mark dot="top" />,
};

const ToastContext = createContext<ToastApi | null>(null);

/** Shows a toast: success, error or info, that opens, tells and goes away by itself. */
export const useToast = (): ToastApi => {
  const api = useContext(ToastContext);
  if (!api) {
    throw new Error("useToast needs a ToastProvider above it");
  }
  return api;
};

/** One toast: goes away by itself after its time, paused while the mouse is on it or the keyboard is inside it. */
const ToastItem = ({ toast, onClose }: { toast: Toast; onClose: (id: number) => void }) => {
  const { t } = useTranslation();
  const [paused, setPaused] = useState(false);
  const left = useRef(toast.duration ?? DURATION[toast.kind]);
  const startedAt = useRef(Date.now());

  useEffect(() => {
    if (paused || toast.leaving) return;
    startedAt.current = Date.now();
    const timer = window.setTimeout(() => onClose(toast.id), left.current);
    return () => {
      window.clearTimeout(timer);
      left.current -= Date.now() - startedAt.current;
    };
  }, [paused, toast.leaving, toast.id, onClose]);

  return (
    <Item
      $kind={toast.kind}
      $leaving={toast.leaving}
      role={toast.kind === "error" ? "alert" : "status"}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}>
      <span className="icon" aria-hidden="true">
        {ICONS[toast.kind]}
      </span>
      <span className="text">
        <span className="title">{toast.title}</span>
        {toast.description && <span className="description">{toast.description}</span>}
      </span>
      {toast.action && (
        <button
          type="button"
          className="action"
          onClick={() => {
            toast.action?.onClick();
            onClose(toast.id);
          }}>
          {toast.action.label}
        </button>
      )}
      <button
        type="button"
        className="close"
        aria-label={t("components.toast.close")}
        onClick={() => onClose(toast.id)}>
        {/* a drawn X, centered in the button (the "×" of the font sits below its middle) */}
        <CloseIcon width={9} height={9} color="currentColor" />
      </button>
    </Item>
  );
};

/**
 * Keeps the toasts of the app and shows them at the top right (on a phone, at the bottom, above its nav): at most 3,
 * a new one pushing the oldest out.
 */
export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts(shown => shown.map(toast => (toast.id === id ? { ...toast, leaving: true } : toast)));
    window.setTimeout(() => setToasts(shown => shown.filter(toast => toast.id !== id)), LEAVE_MS);
  }, []);

  const api = useMemo<ToastApi>(() => {
    const show =
      (kind: ToastKind) =>
      (title: string, options: ToastOptions = {}) => {
        const id = nextId.current++;
        setToasts(shown => [
          ...shown.filter(toast => !toast.leaving).slice(-(MAX_SHOWN - 1)),
          { ...options, id, kind, title, leaving: false },
        ]);
        return id;
      };
    return { success: show("success"), error: show("error"), info: show("info"), dismiss };
  }, [dismiss]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      {/* always in the page, so a screen reader hears what is added to it (a region made with the toast is not reliably read) */}
      <Viewport aria-live="polite" aria-relevant="additions">
        {toasts.map(toast => (
          <ToastItem key={toast.id} toast={toast} onClose={dismiss} />
        ))}
      </Viewport>
    </ToastContext.Provider>
  );
};
